import { useCallback, useEffect, useState } from 'react';

import { FormProvider, useForm } from 'react-hook-form';

import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import {
  MembershipRead,
  MembershipWrite,
  PatchedMembershipWrite,
} from 'src/common/external/bambi-api/bambiApi';
import { useModal } from 'src/common/modals/useModal';
import formatServerError from 'src/common/util/serverErrorFormatter';

import { setSelectedMember, setShowMemberForm } from '../member.slice';
import { setShowMemberProfilePromptModal } from '../team.slice';
import { AddEditModal } from './AddEditModal';
import { assignDefaultFormValues } from './assignDefaultFormValues';
import { FormValues } from './FormValues';
import { MemberDuplicateNameModal } from './MemberDuplicateNameModal';
import { MemberProfilePromptModal } from './MemberProfilePromptModal';
import { MemberRestorePromptModal } from './MemberRestorePromptModal';
import { useMemberRepository } from './useMemberRepository';

export function MemberForm() {
  const member = useAppSelector((state) => state.member.selectedMember);

  const defaultValues = assignDefaultFormValues(member);
  const methods = useForm<FormValues>({
    defaultValues,
  });

  const open = useAppSelector((state) => state.member.showMemberForm);
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [serverErrors, setServerErrors] = useState<string | null>('');
  const [highlightEmailField, setHighlightEmailField] = useState(false);

  const duplicateModalProps = useModal();
  const [duplicateMembership, setDuplicateMembership] =
    useState<MembershipRead | null>(null);

  const duplicateNameModalProps = useModal();
  const [duplicateNameMembership, setDuplicateNameMembership] =
    useState<MembershipRead | null>(null);

  const {
    create: createMember,
    delete: deleteMember,
    restore: restoreMember,
    update: updateMember,
    findDuplicateByEmail: findDuplicateMemberByEmail,
    findDuplicateByName: findDuplicateMemberByName,
  } = useMemberRepository(member);

  const onSubmit = async (data: FormValues, skipDuplicateChecks = false) => {
    setLoading(true);

    if (!skipDuplicateChecks) {
      // Reset
      setDuplicateMembership(null);
      setDuplicateNameMembership(null);
      duplicateModalProps.setOpen(false);
      duplicateNameModalProps.setOpen(false);

      // Check for duplicate member by email

      const duplicateByEmailResponse = await findDuplicateMemberByEmail(
        data.email
      );
      if (
        duplicateByEmailResponse?.duplicateMember &&
        duplicateByEmailResponse.duplicateMemberIsDeleted
      ) {
        // Since we've detected a duplicate member, prevent create/update attempt
        setDuplicateMembership(duplicateByEmailResponse.duplicateMember);
        setLoading(false);
        // Show the restore modal
        duplicateModalProps.setOpen(true);
        return;
      }

      // Check for duplicate member by name
      const duplicateByNameResponse = await findDuplicateMemberByName(
        data.first_name,
        data.last_name
      );

      const duplicateMemberByName = duplicateByNameResponse?.duplicateMember;

      if (
        duplicateMemberByName &&
        // Ignore the currently edited member when checking for duplicates
        (!member || duplicateMemberByName.id !== member.id)
      ) {
        // Since we've detected a duplicate member, prevent create/update attempt
        setDuplicateNameMembership(duplicateMemberByName);
        setLoading(false);
        // Show the duplicate-name modal
        duplicateNameModalProps.setOpen(true);
        return;
      }
    }

    try {
      if (member) {
        // TODO: Format payers select options to whatever the api needs
        const patchedMembership: PatchedMembershipWrite = {
          ...data,
          role: data.role?.value,
        };

        await updateMember(patchedMembership);
      } else {
        // TODO: Format payers select options to whatever the api needs
        const membership: MembershipWrite = {
          ...data,
          role: data.role?.value,
        };

        const newMember = await createMember(membership);

        if (newMember.role === 'associate') {
          dispatch(
            setShowMemberProfilePromptModal({
              open: true,
              member: newMember,
            })
          );
        }
      }

      setServerErrors(null);

      methods.reset();
    } catch (error) {
      setServerErrors(formatServerError(error));
    }
    setLoading(false);
  };

  const handleDeleteMember = async () => {
    try {
      await deleteMember();
      setServerErrors(null);
    } catch (error) {
      setServerErrors(formatServerError(error));
    }
  };

  const handleRestoreMember = useCallback(
    async (targetMember?: MembershipRead) => {
      try {
        await restoreMember(targetMember);
        setServerErrors(null);

        duplicateModalProps.setOpen(false);
      } catch (error) {
        setServerErrors(formatServerError(error));
      }
    },
    [restoreMember, setServerErrors, duplicateModalProps]
  );

  useEffect(() => {
    if (open) {
      methods.reset(defaultValues);
      setHighlightEmailField(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, setHighlightEmailField]);

  return (
    <FormProvider {...methods}>
      <AddEditModal
        open={open}
        setOpen={(val) => dispatch(setShowMemberForm(val))}
        onCancel={() => {
          setServerErrors(null);
          dispatch(setShowMemberForm(false));
          methods.reset();
        }}
        onDelete={handleDeleteMember}
        onRestore={handleRestoreMember}
        onClose={() => {
          setServerErrors(null);
          dispatch(setSelectedMember(null));
          methods.reset();
        }}
        onSubmit={methods.handleSubmit((data) => onSubmit(data, false))}
        errors={methods.formState.errors}
        isReadyToSubmit={Object.keys(methods.formState.errors).length < 1}
        loading={loading}
        control={methods.control}
        serverErrors={serverErrors}
        highlightEmailField={highlightEmailField}
      />
      <MemberProfilePromptModal />
      <MemberRestorePromptModal
        {...duplicateModalProps}
        membership={duplicateMembership}
        onRestoreClick={handleRestoreMember}
      />
      <MemberDuplicateNameModal
        {...duplicateNameModalProps}
        membership={duplicateNameMembership}
        onCreate={() => {
          duplicateNameModalProps.setOpen(false);
          methods.handleSubmit((data) => onSubmit(data, true))();
        }}
        onEdit={(membership) => {
          duplicateNameModalProps.setOpen(false);
          dispatch(setSelectedMember(membership));
          methods.reset(assignDefaultFormValues(membership));
          setHighlightEmailField(true);
        }}
        onRestore={async (membership) => {
          setLoading(true);
          duplicateNameModalProps.setOpen(false);
          await handleRestoreMember(membership);
          setLoading(false);
        }}
      />
    </FormProvider>
  );
}
